import * as _ from 'lodash';

/**
 * fetchers that are custom to FOSSA ('custom', 'archive', 'user', etc.)
 */
export const fossaFetchersMap = Object.freeze({
  archive: 'Archive',
  custom: 'Custom (provided build)',
  url: 'Global URL',
  'url-private': 'Private URL',
  user: 'User Defined',
  path: 'Local',
  sbom: 'SBOM',
  csbinary: 'Binary Decomposition Component',
  binary: 'Uploaded Binary',
});

export const CodeSentryFetcherName = 'csbinary';
export const BinaryFetcherName = 'binary';

type FOSSAFetcher = keyof (typeof fossaFetchersMap);

type FOSSAFetcherLabel = typeof fossaFetchersMap[FOSSAFetcher];

/**
 * fetchers that have a backing 3rd party registry/ecosystem. NOT FOSSA specific fetchers like 'custom' or 'archive'.
 * NOTE: DO NOT ADD ANY FOSSA SPECIFIC FETCHERS HERE (custom, archive, etc.).
 * This is used to serve package data in the global package inventory, so we don't want unexpected results to show up.
 */
export const thirdPartyFetchersMap = Object.freeze({
  apk: 'APK',
  bower: 'Bower',
  cargo: 'Cargo',
  cart: 'Carthage',
  comp: 'Composer',
  conda: 'Conda',
  cpan: 'CPAN',
  cran: 'CRAN',
  deb: 'Debian',
  gem: 'RubyGem',
  git: 'Git',
  go: 'Golang',
  hackage: 'Hackage',
  hex: 'Hex',
  mvn: 'Maven',
  npm: 'NPM',
  nuget: 'Nuget',
  pip: 'Pip',
  pod: 'CocoaPods',
  pub: 'Pub',
  rpm: 'RPM',
  'rpm-generic': 'RPM',
  swift: 'Swift',
});

/**
 * ThirdPartyFetcher is the type of all third party fetchers.
 */
export type ThirdPartyFetcher = keyof (typeof thirdPartyFetchersMap);

/**
 * ThirdPartyFetcherLabel is the human-readable label for a third party fetcher.
 */
export type ThirdPartyFetcherLabel = typeof thirdPartyFetchersMap[ThirdPartyFetcher];

/**
 * Fetcher is the type of all fetchers
 */
export type Fetcher = FOSSAFetcher | ThirdPartyFetcher;

/**
 * Any human-readable label for a fetcher.
 */
type FetcherLabel = FOSSAFetcherLabel | ThirdPartyFetcherLabel;

//  This way we don't need to Object.keys it every time.
export const validThirdPartyFetchers = Object.keys(thirdPartyFetchersMap) as ThirdPartyFetcher[];

// Return a distinct fetcher list to prevent the same fetcher value appearing twice in the UI.
// If we don't remove the duplicate, the UI will show two 'RPM' options.
const duplicateFetchers = new Set<ThirdPartyFetcher>(['rpm']);
export const distinctFetchers =
  validThirdPartyFetchers.filter(fetcher => !duplicateFetchers.has(fetcher));

/**
 * Determines if a given value is a valid thirdPartyFetcher. Acts as a type guard so callers
 * receive a narrowed `ThirdPartyFetcher` on the truthy branch — pluggable directly into
 * `Array.prototype.filter` and Zod's `.refine()`.
 *
 * @param fetcher - Any value to test.
 */
export const isValidFetcher = (fetcher: unknown): fetcher is ThirdPartyFetcher =>
  validThirdPartyFetchers.includes(fetcher as ThirdPartyFetcher);

/**
 * allFetchersMap is a map of ALL available fetchers in FOSSA to their display name
 */
export const allFetchersMap: Record<Fetcher, FetcherLabel> = Object.freeze({
  ...thirdPartyFetchersMap,
  ...fossaFetchersMap,
});

/**
 * fetcherToPlainText takes in a fetcher string and returns a plain text string describing the fetcher:
 * 'mvn' -> 'Maven', 'pod' => 'Cocoapods'
 */
export function fetcherToPlainText(fetcher: Fetcher): FetcherLabel | undefined {
  return allFetchersMap[fetcher];
}

/**
 * languageToFetcherMap maps a programming language to a list of potential fetchers that could include that language
 */
export const languageToFetcherMap = Object.freeze({
  '.net': ['nuget'],
  'c#': ['nuget'],
  java: ['mvn'],
  javascript: ['npm', 'bower'],
  go: ['git'],
  php: ['comp'],
  python: ['pip'],
  r: ['cran'],
  ruby: ['gem'],
  rust: ['cargo'],
});

/**
 * linuxFetchers is a readonly list of all linux fetchers. These fetchers fetch data using the Knowledge API, which queries Ramjet (as of 5/20/22)
 */
type LinuxFetcher = 'apk' | 'deb' | 'rpm-generic';

export const linuxFetchers: readonly LinuxFetcher[] = Object.freeze([
  'apk',
  'deb',
  'rpm-generic',
]);

/**
 * isLinuxFetcher returns true if @fetcher is a "linux" fetcher. This includes fetchers fetch data using the Knowledge API, which queries Ramjet (as of 5/20/22)
 */
export function isLinuxFetcher(fetcher?: string | null): fetcher is LinuxFetcher {
  if (!fetcher) {
    return false;
  }

  const lowerCaseFetcher = fetcher.toLowerCase();
  return linuxFetchers.some(f => f === lowerCaseFetcher);
}

export const linuxDistrosByFetcher = Object.freeze({
  apk: [{ value: 'alpine', label: 'Alpine' }],
  deb: [{ value: 'ubuntu', label: 'Ubuntu' }, { value: 'debian', label: 'Debian' }],
  'rpm-generic': [
    { value: 'redhat', label: 'Redhat' },
    { value: 'centos', label: 'CentOS' },
    { value: 'oraclelinux', label: 'Oracle Linux' },
    { value: 'rhel', label: 'Redhat Enterprise Linux' },
    { value: 'fedora', label: 'Fedora' },
    { value: 'sles', label: 'SUSE Linux Enterprise Server' },
  ],
});

export const commonLinuxArchs = Object.freeze([
  'all',
  'noarch',
  'amd64',
  'arm64',
  'x86_64',
  'aarch64',
]);

/**
 * externalApiSanitizedFetchers is a list of fetchers that make external API calls during sanitization for removeRegistryFromLocator.
 */
export const externalApiSanitizedFetchers: Set<Fetcher> = new Set<Fetcher>(['npm']);

/**
 * Fetchers whose package names are case-insensitive in their ecosystem.
 * VulnCalculation rows for the same logical revision can be stored under
 * either the original or lowercased package name, so lookups (in VulnScanner
 * and SafeVersionManager) need to consider both casings.
 */
export const caseInsensitiveFetchers: ReadonlySet<string> = new Set(['pip']);

// Sort the language dropdown options, and turn them into value+label pairs.
// Remove any fetchers that are not distinct.
const sortedDropdownOptions = _.sortBy(
  Object.entries(thirdPartyFetchersMap)
    .map(([value, label]) => ({ value: value as ThirdPartyFetcher, label })),
  elem => elem.label,
);
export const FETCHER_DROPDOWN_OPTIONS =
  sortedDropdownOptions.filter(({ value }) => distinctFetchers.includes(value));
